import { deepmerge } from "@mui/utils";
import MuiIconButton from "@mui/material/IconButton";
import { alpha, styled, type Theme, type CSSObject } from "@mui/material/styles";

import { SIZE_TOKENS, type TonePalette, resolveTonePalette } from "./tokens";

import type { IconButtonSize, IconButtonTone, IconButtonVariant } from "./types";

type SizeToken = (typeof SIZE_TOKENS)[IconButtonSize];

/** Área tocável mínima (HIG/WCAG 2.5.5): 44px, ou o tamanho do botão se maior. */
const MIN_TOUCH_TARGET = 44;

/**
 * Cor do anel de foco. No tom `neutral` a base é `grey[300]` — um anel cinza
 * quase invisível. Foco precisa ser sempre bem visível, então neutro ancora na
 * cor de contraste da superfície (branco no dark, quase-preto no light).
 */
function focusRingColor(theme: Theme, palette: TonePalette, tone: IconButtonTone): string {
  return tone === "neutral" ? theme.palette.text.primary : palette.base;
}

/** Esqueleto comum a todas as variantes (tamanho, foco, disabled, ícone). */
function baseStyles(
  theme: Theme,
  palette: TonePalette,
  tone: IconButtonTone,
  t: SizeToken
): CSSObject {
  const ringColor = focusRingColor(theme, palette, tone);
  return {
    position: "relative",
    width: t.side,
    height: t.side,
    padding: 0,
    borderRadius: theme.spacing(1.25),
    color: palette.base,
    transition: theme.transitions.create(
      ["background-color", "border-color", "color", "transform"],
      { duration: theme.transitions.duration.shorter }
    ),
    "& > svg, & [data-icon]": {
      fontSize: t.iconFontSize,
      width: t.iconFontSize,
      height: t.iconFontSize,
    },

    // Estende o alvo tocável pro mínimo de 44px sem inflar o desenho — só em
    // ponteiro grosso (dedo). O botão pode ter 32/40px de visual e 44px de toque.
    "@media (pointer: coarse)": {
      "&::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: `max(100%, ${MIN_TOUCH_TARGET}px)`,
        height: `max(100%, ${MIN_TOUCH_TARGET}px)`,
      },
    },

    // Feedback tátil de pressão (o ripple do Material está desligado). Fica no
    // base e é combinado via deepmerge com o `:active` de cor de cada variante.
    "&:active:not(.Mui-disabled)": {
      transform: "scale(0.94)",
    },

    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 3px ${alpha(ringColor, 0.4)}`,
    },

    "&.Mui-disabled": {
      opacity: 0.5,
    },

    // Respeita quem pede menos movimento: sem transição nem press-scale.
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:active:not(.Mui-disabled)": { transform: "none" },
    },
  };
}

/**
 * Estilo específico de cada variante. **Strategy map**: adicionar uma variante
 * é adicionar uma entrada aqui (+ uma no union de `IconButtonVariant`) — sem
 * tocar nas demais nem em cadeia de `if`/`switch`. Hover fica sob
 * `@media (hover: hover)` pra não "grudar" após o tap no touch.
 */
const VARIANT_STYLES: Record<
  IconButtonVariant,
  (palette: TonePalette, theme: Theme, tone: IconButtonTone) => CSSObject
> = {
  ghost: (palette) => ({
    backgroundColor: "transparent",
    "@media (hover: hover)": {
      "&:hover": { backgroundColor: alpha(palette.base, 0.08) },
    },
    "&:active": { backgroundColor: alpha(palette.base, 0.14) },
  }),
  tinted: (palette) => ({
    backgroundColor: palette.bg,
    "@media (hover: hover)": {
      "&:hover": { backgroundColor: palette.bgHover },
    },
    "&:active": { backgroundColor: palette.bgActive },
  }),
  solid: (palette, theme) => ({
    backgroundColor: palette.base,
    color: theme.palette.getContrastText(palette.base),
    "@media (hover: hover)": {
      "&:hover": { backgroundColor: palette.base, filter: "brightness(1.08)" },
    },
    "&:active": { backgroundColor: palette.base, filter: "brightness(0.94)" },
    "&.Mui-disabled": {
      opacity: 0.5,
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  }),
  "ghost-bordered": (palette, theme, tone) => {
    const isLight = theme.palette.mode === "light";
    // Tom neutro = "chrome": cor de contraste da superfície (text.primary),
    // não o cinza fixo — assim funciona como header chip em dark e light.
    // Tons coloridos seguem a paleta normalmente.
    const c = tone === "neutral" ? theme.palette.text.primary : palette.base;
    return {
      color: c,
      backgroundColor: alpha(c, isLight ? 0.04 : 0.08),
      border: "1px solid",
      borderColor: alpha(c, isLight ? 0.12 : 0.15),
      "@media (hover: hover)": {
        "&:hover": { backgroundColor: alpha(c, isLight ? 0.08 : 0.12) },
      },
      "&:active": { backgroundColor: alpha(c, isLight ? 0.12 : 0.16) },
    };
  },
  outline: (palette, theme, tone) => {
    const isLight = theme.palette.mode === "light";
    // Mesmo "chrome" do ghost-bordered no tom neutro, mas sem preenchimento em
    // repouso: só o contorno desenha o botão; o fundo só aparece no hover.
    const c = tone === "neutral" ? theme.palette.text.primary : palette.base;
    return {
      color: c,
      backgroundColor: "transparent",
      border: "1px solid",
      borderColor: alpha(c, isLight ? 0.16 : 0.2),
      "@media (hover: hover)": {
        "&:hover": {
          backgroundColor: alpha(c, isLight ? 0.06 : 0.08),
          borderColor: alpha(c, isLight ? 0.24 : 0.3),
        },
      },
      "&:active": { backgroundColor: alpha(c, isLight ? 0.1 : 0.12) },
    };
  },
};

/**
 * Estilo do estado **ligado** (toggle `selected`). Cada variante intensifica o
 * seu próprio look — mantendo o tom, mas deixando claro que está "on".
 */
const SELECTED_STYLES: Record<
  IconButtonVariant,
  (palette: TonePalette, theme: Theme, tone: IconButtonTone) => CSSObject
> = {
  // Ghost ligado adota o preenchimento tênue + a cor do tom (some do "apagado").
  ghost: (palette) => ({
    backgroundColor: alpha(palette.base, 0.16),
    color: palette.base,
  }),
  // Tinted ligado aprofunda o fundo.
  tinted: (palette) => ({ backgroundColor: palette.bgActive }),
  // Solid já é cheio; ligado ganha um anel interno pra sinalizar pressionado.
  solid: (palette, theme) => ({
    boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.common.white, 0.5)}`,
  }),
  "ghost-bordered": (palette, theme, tone) => {
    const isLight = theme.palette.mode === "light";
    const c = tone === "neutral" ? theme.palette.text.primary : palette.base;
    return {
      color: c,
      backgroundColor: alpha(c, isLight ? 0.14 : 0.18),
      borderColor: alpha(c, isLight ? 0.28 : 0.32),
    };
  },
  // Outline ligado ganha preenchimento tênue — é o único jeito de sinalizar
  // "on" sem fundo em repouso.
  outline: (palette, theme, tone) => {
    const isLight = theme.palette.mode === "light";
    const c = tone === "neutral" ? theme.palette.text.primary : palette.base;
    return {
      color: c,
      backgroundColor: alpha(c, isLight ? 0.12 : 0.16),
      borderColor: alpha(c, isLight ? 0.28 : 0.32),
    };
  },
};

type StyledExtra = {
  $variant: IconButtonVariant;
  $tone: IconButtonTone;
  $size: IconButtonSize;
  $selected: boolean;
};

export const Root = styled(MuiIconButton, {
  name: "DsIconButton",
  slot: "Root",
  shouldForwardProp: (prop) => typeof prop === "string" && !prop.startsWith("$"),
})<StyledExtra>(({ theme, $variant, $tone, $size, $selected }): CSSObject => {
  const palette = resolveTonePalette(theme, $tone);
  // deepmerge preserva os `:active`/media aninhados das duas camadas — o
  // press-scale do base convive com o `:active` de cor da variante.
  const merged = deepmerge(
    baseStyles(theme, palette, $tone, SIZE_TOKENS[$size]),
    VARIANT_STYLES[$variant](palette, theme, $tone)
  );
  return $selected
    ? deepmerge(merged, SELECTED_STYLES[$variant](palette, theme, $tone))
    : merged;
});

/**
 * Contador do canto (`badge`). Escuro sobre qualquer tom, com anel da cor do
 * papel para se descolar do contorno do botão: o mesmo selo de canto do
 * "Eventos do time" na súmula. Números tabulares:
 * "9" e "10" não mudam a largura de um jeito que pule.
 */
export const BadgeCount = styled("span", { name: "DsIconButton", slot: "Badge" })(
  ({ theme }): CSSObject => ({
    position: "absolute",
    top: -6,
    right: -6,
    minWidth: 18,
    height: 18,
    padding: theme.spacing(0, 0.625),
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
  })
);
